import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import {
  A11yButton,
  FilterChips,
  SearchInput,
  SingleChoice,
  MultipleChoice,
  FilterChip,
} from '@fe-learner-common';
import { DurationChoice } from './DurationChoice';
import { DateRangeChoice } from './DateRangeChoice';

interface FilterOption {
  filterKey: string;
  type: 'singleChoice' | 'multipleChoice';
}

export type Duration = {
  min: number;
  max: number;
};

export type DateRange = {
  start?: string | null;
  end?: string | null;
};

interface SingleChoice extends FilterOption {
  type: 'singleChoice';
  selected: string;
  label: string;
  items: { key: string; label: string }[];
}

type MultipleChoice = {
  type: 'multipleChoice';
  selectedItems: Set<string>;
  filterKey: string;
  label: string;
  items: { key: string; label: string }[];
};

type DurationChoice = {
  type: 'durationChoice';
  selectedItems: { min: number; max: number };
  filterKey: string;
  label: string;
  items: { min: number; max: number };
};

type DateRangeChoice = {
  type: 'dateRangeChoice';
  selectedItems: DateRange;
  filterKey: string;
  label: string;
  items: DateRange;
};

export type TFilterConfig = {
  search?: {
    value: string;
    placeholder: string;
  };
  options: (MultipleChoice | SingleChoice | DurationChoice | DateRangeChoice)[];
};

type FilterProps = {
  filterConfig: TFilterConfig | null;
  onClearFilter: () => void;
  onChangeFilter: (
    key: string,
    value: Set<string> | string | Duration | DateRange,
  ) => void;
  onViewResults: () => void;
  nrOfResults?: number;
  filterChips?: FilterChip[];
  resultsLoading?: boolean;
};

const handleFilterSet = (set: Set<string>, value: string) => {
  if (!set) return;

  if (set.has(value)) {
    set.delete(value);
  } else {
    set.add(value);
  }
  return set;
};

export const Filter: FC<FilterProps> = ({
  filterConfig,
  onClearFilter,
  onChangeFilter,
  onViewResults,
  nrOfResults,
  filterChips,
  resultsLoading,
}) => {
  const intl = useIntl();
  const [renderPicker, setRenderPicker] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { register, setValue } = useForm();
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      setRenderPicker(true);
    }
  }, [containerRef]);

  const handleClearAll = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }

    onClearFilter();
  };

  const handleSearch = (value: string) => {
    setValue('search', value);
    onChangeFilter('search', value);
  };

  const handleOptionChange = (
    option: SingleChoice | MultipleChoice | DurationChoice | DateRangeChoice,
    value: string | Duration | DateRange,
  ) => {
    let filterValue;
    if (option.type === 'singleChoice') {
      filterValue = value;
    } else if (option.type === 'multipleChoice') {
      filterValue = handleFilterSet(option.selectedItems, value as string);
    } else if (option.type === 'durationChoice') {
      filterValue = value as Duration;
    } else if (option.type === 'dateRangeChoice') {
      filterValue = value as DateRange;
    }
    if (filterValue) {
      setValue(option.filterKey, value);
      onChangeFilter(option.filterKey, filterValue);
    }
  };

  const handleChipRemove = (groupKey: string, filterKey: string) => {
    if (groupKey === 'search') {
      setValue('search', '');
      onChangeFilter('search', '');
    } else {
      const option = filterConfig?.options.find(
        (x) => x.filterKey === groupKey,
      );
      if (option) {
        handleOptionChange(option, filterKey);
      }
    }
  };

  if (!filterConfig) {
    return null;
  }

  return (
    <div
      className="flex max-h-full flex-col overflow-hidden"
      ref={containerRef}
    >
      <div className="overflow-auto" style={{ scrollbarGutter: 'stable' }}>
        <FilterChips
          chips={filterChips}
          nrOfResults={nrOfResults || 0}
          onRemoveFilter={handleChipRemove}
          onClearAll={handleClearAll}
        />
        {filterConfig.search && (
          <div className="p-5">
            <SearchInput
              className=""
              {...register('search')}
              id="searchCourses"
              inputRef={searchInputRef}
              defaultValue={filterConfig?.search.value || ''}
              texts={{
                placeholder: filterConfig?.search.placeholder || '',
                clearSearchAriaLabel: intl.formatMessage({
                  id: 'learner.common.search.clearAriaLabel',
                }),
              }}
              onSearch={(value) => {
                handleSearch(value);
              }}
            />
          </div>
        )}

        <div className="space-y-6 p-2">
          {filterConfig.options.map((option) => {
            if (option.type === 'singleChoice') {
              return (
                <SingleChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  onChange={(value: string) => {
                    handleOptionChange(option, value);
                  }}
                  label={option.label}
                  selected={option.selected}
                  items={option.items.map(({ key, label }) => ({
                    key,
                    label,
                  }))}
                />
              );
            }

            if (option.type === 'multipleChoice') {
              return (
                <MultipleChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  onChange={(value: string) => {
                    handleOptionChange(option, value);
                  }}
                  label={option.label}
                  items={option.items.map(({ key, label }) => ({
                    key,
                    label,
                    selected: option.selectedItems?.has(key) ?? false,
                  }))}
                />
              );
            }

            if (option.type === 'durationChoice') {
              return (
                <DurationChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  min={option.items.min}
                  max={option.items.max}
                  value={[
                    option.selectedItems.min || option.items.min,
                    option.selectedItems.max || option.items.max,
                  ]}
                  onChange={(range) => {
                    handleOptionChange(option, {
                      min: range[0],
                      max: range[1],
                    });
                  }}
                  label={option.label}
                  ariaLabelMinValue={intl.formatMessage({
                    id: 'learner.common.filter.hours.minimum',
                  })}
                  ariaLabelMaxValue={intl.formatMessage({
                    id: 'learner.common.filter.hours.maximum',
                  })}
                />
              );
            }

            if (option.type === 'dateRangeChoice' && renderPicker) {
              return (
                <DateRangeChoice
                  key={option.filterKey}
                  {...register(option.filterKey)}
                  filterKey={option.filterKey}
                  containerRef={containerRef}
                  allowedRange={{
                    start: option.items.start,
                    end: option.items.end,
                  }}
                  value={{
                    start: option.selectedItems.start,
                    end: option.selectedItems.end,
                  }}
                  onChange={(range: DateRange) => {
                    handleOptionChange(option, range);
                  }}
                  label={option.label}
                />
              );
            }
          })}
        </div>
      </div>

      <div className="mt-auto flex flex-wrap gap-2 border-t-2 border-solid border-neutral-100 p-5">
        <button
          className="l-btn l-btn-md l-btn-secondary flex-1 whitespace-nowrap"
          onClick={handleClearAll}
        >
          {intl.formatMessage({ id: 'learner.common.filter.reset' })}
        </button>
        <A11yButton
          className="l-btn l-btn-md l-btn-primary flex-1 whitespace-nowrap"
          onClick={onViewResults}
          isLoading={resultsLoading}
          disabled={resultsLoading}
        >
          {intl.formatMessage(
            { id: 'learner.common.filter.viewResults' },
            { nr: nrOfResults },
          )}
        </A11yButton>
      </div>
    </div>
  );
};
